/**
 * 정규식 디스플레이/변형 스크립트 — 결정 #21 렌더 파이프라인의 정규식 단계이자
 * 결정 #17(권한 등급제)의 "safe" 등급 변형이다.
 *
 * 적용 순서(결정 #21, 순서가 곧 보안):
 *   Roqeet 인라인 전개 → **정규식 스크립트** → 마크다운 파싱 → 살균 → 렌더
 *
 * 정규식 스크립트는 ST `customscript`(`in`/`out`/placement)와 정렬된다
 * (`docs/risu-migration.md` §3.2). placement로 입력/출력/표시/월드인포 단계를
 * 구분한다.
 *
 * 보안(결정 #17 / risu-migration §3.2):
 *  - ReDoS 위험: 사용자 임의 정규식은 저사양 기기를 멈출 수 있다. 순수 JS에는
 *    정규식 타임아웃이 없으므로 여기서는 (a) 치환 횟수 상한, (b) 입력 길이 상한,
 *    (c) 예외 격리로 1차 방어한다. 진짜 방어(RE2-WASM)는 후속 과제다.
 *  - 출력이 HTML을 만들 수 있으므로, 이 단계는 반드시 살균(rehype-sanitize)보다
 *    "먼저" 실행되어야 한다(결정 #21). 살균은 렌더 단계의 책임이다.
 */
import type { RegexPlacement, Script } from "../domain/index.js";

/** 안전장치: 한 스크립트가 처리할 최대 입력 길이(초과 시 스킵). */
const MAX_INPUT_LENGTH = 100_000;

/** 정규식 스크립트 1개를 텍스트에 적용한다. 실패/위험 시 원문을 그대로 반환. */
function applyOne(script: Script, text: string): string {
  const def = script.regex;
  if (!def) return text;
  if (text.length > MAX_INPUT_LENGTH) return text; // ReDoS 1차 방어

  let flags = def.flags || "";
  // 전역 치환이 기본 기대치(ST 동작). g가 없으면 더해 모든 매치를 처리.
  if (!flags.includes("g")) flags += "g";

  let re: RegExp;
  try {
    re = new RegExp(script.source, flags);
  } catch {
    // 잘못된 정규식은 무시(원문 보존).
    return text;
  }

  try {
    // 치환 문자열의 ST 스타일 토큰($1 등)은 JS 정규식과 호환된다.
    return text.replace(re, def.replacement);
  } catch {
    return text;
  }
}

/**
 * 주어진 placement에 해당하는 활성 정규식 스크립트를 순서대로 적용한다.
 *
 * @param text       변형할 텍스트(Roqeet 인라인 전개 후).
 * @param scripts    후보 스크립트(캐릭터/전역). language!=="regex"는 무시.
 * @param placement  현재 파이프라인 단계.
 */
export function applyRegexScripts(
  text: string,
  scripts: Script[],
  placement: RegexPlacement,
): string {
  let out = text;
  for (const script of scripts) {
    if (script.language !== "regex") continue;
    if (!script.enabled) continue;
    if (!script.regex) continue;
    if (!script.regex.placement.includes(placement)) continue;
    out = applyOne(script, out);
  }
  return out;
}

/**
 * 표시 전용(display-only) 정규식 스크립트만 적용하는 편의 함수.
 * 메시지 렌더 파이프라인(결정 #21)에서 마크다운 파싱 직전에 호출한다.
 * 저장된 내용은 바꾸지 않고 화면 표시만 변형한다.
 */
export function applyDisplayRegex(text: string, scripts: Script[]): string {
  return applyRegexScripts(text, scripts, "display-only");
}
